import { useCallback } from 'react';
import { useNavigate } from 'react-router';

import { UnpublishedListing } from 'components/UnpublishedListing/UnpublishedListing';
import { useSetDocumentTitle } from 'hooks/useSetDocumentTitle';
import { useI18n } from 'i18n/hooks/useI18n';
import type { ApiOrg } from 'modules/listing/orgs/api/types/ApiOrg';
import type { ApiUnpublishedOrg } from 'modules/listing/orgs/api/types/ApiUnpublishedOrg';
import { useSearchContextHeader } from 'modules/search/containers/SearchContextHeader/useSearchContextHeader';
import { getParsedSearchQuery } from 'modules/search/helpers/getParsedSearchQuery';
import type { SearchPageLocationState } from 'modules/search/pages/Search/types/SearchPageLocationState';
import { searchGetOrgsRoutePath } from 'modules/search/routing/helpers/searchGetOrgsRoutePath';

type Props = {
  unpublishedOrg: ApiOrg | ApiUnpublishedOrg;
};

export function UnpublishedOrgPage({ unpublishedOrg }: Props) {
  const { i18n } = useI18n();

  const { searchContext } = useSearchContextHeader({
    currentObjectId: unpublishedOrg.id,
  });

  useSetDocumentTitle(i18n('Listing Unpublished'));

  const navigate = useNavigate();
  const search = useCallback(
    (query: string) => {
      const { parsedQuery, remoteKeywords } = getParsedSearchQuery({ query });

      navigate(
        searchGetOrgsRoutePath({
          lang: CURRENT_LOCALE(),
          q: parsedQuery,
        }),
        {
          state: {
            searchFrom: 'Unpublished Org Page',
            remoteKeywords,
          } satisfies SearchPageLocationState,
        },
      );
    },
    [navigate],
  );

  return (
    <UnpublishedListing
      searchContext={searchContext}
      footerTitle={i18n('Search Orgs')}
      seeMoreText={i18n(
        'Search below for an organization or cause that interests you.',
      )}
      searchFormAnalyticsTitle="[main] View Org Page (Unpublished) - search"
      onSubmit={({ values }) => search(values.query)}
    />
  );
}
